import { useFeatureFlag } from "@jsq/flags-react";
import { ComplianceCaseTypeEnum } from "~/graphql/supergraphEnums";
import { isFatcaCaseType, isStandaloneCaseType } from "./utils";

export const useEnableAiScreening = () => {
  return useFeatureFlag("lnScreeningAgent", false);
};

export const useComplianceProfileRefreshTab = () => {
  return useFeatureFlag("showComplianceProfileRefreshTab", false);
};

export const useComplianceEntityOnboardingEnabled = () => {
  return useFeatureFlag("complianceEntityOnboardingEnabled", false);
};

export const useEnableClientLevelPermissions = () => {
  return useFeatureFlag("enableClientLevelPermissions", false);
};

export const usePicrRevampFeatureFlag = () => {
  return useFeatureFlag("picrRevamp", false);
};

export const useComplianceAiExtraction = () => {
  return useFeatureFlag("complianceAiExtraction", false);
};

export const useDocumentRefreshFeatureFlag = () => {
  return useFeatureFlag("enableDocumentRefresh", false);
};

export const useComplianceCiC = () => {
  return useFeatureFlag("enableComplianceCiC", false);
};

export const useReusableProfilesFeatureFlag = () => {
  return useFeatureFlag("complianceReusableProfilesEnabled", false);
};

export const useDragAndDropProfilesFeatureFlag = () => {
  return useFeatureFlag("dragAndDropProfiles", false);
};

export const useComplianceFatcaCrsFeatureFlag = () => {
  return useFeatureFlag("complianceFatcaCrs", false);
};

export const useAmlLuxFeatureFlag = () => {
  return useFeatureFlag("enableAmlLux", false);
};

export const useBlockComplianceCaseReopening = () => {
  return useFeatureFlag("blockComplianceCaseReopening", false);
};

export const useGuidedProfileCreation = () => {
  return useFeatureFlag("guidedProfileCreation", false);
};

export const useIOReusableProfiles = () => {
  return useFeatureFlag("ioReusableProfilesEnabled", false);
};

export const useComplianceFatcaCrsStandalone = () => {
  return useFeatureFlag("complianceFatcaCrsStandalone", false);
};

// Transitional cutover guard (COMP-4730): when on, FATCA/CRS resolves from the per-entity
// config; when off, the legacy complianceFatcaCrs* flags apply. Removed in COMP-4735.
export const useComplianceFatcaCrsConfigMigration = () => {
  return useFeatureFlag("complianceFatcaCrsConfigMigration", false);
};

/**
 * Whether FATCA/CRS data collection applies to a case.
 *
 * Under the config migration (COMP-4730), mirrors the BE `is_fatca_crs_enabled_for_case`:
 * the case must be a FATCA-eligible type AND the entity's per-entity `fatcaCrsEnabled` (from
 * `case.entity.complianceSettings.fatcaCrsEnabled`) must be on. When the migration flag is off,
 * the legacy two-flag logic applies (removed in COMP-4735).
 */
export const useIsFatcaCrsEnabledForCase = (
  caseType: ComplianceCaseTypeEnum | null | undefined,
  fatcaCrsEnabled?: boolean | null,
): boolean => {
  const migrationEnabled = useComplianceFatcaCrsConfigMigration();
  const masterEnabled = useComplianceFatcaCrsFeatureFlag();
  const standaloneEnabled = useComplianceFatcaCrsStandalone();

  if (migrationEnabled) {
    return isFatcaCaseType(caseType) && !!fatcaCrsEnabled;
  }

  if (!masterEnabled) return false;
  if (caseType === ComplianceCaseTypeEnum.InvestorOnboarding) return true;
  if (caseType === ComplianceCaseTypeEnum.Remediation || isStandaloneCaseType(caseType)) {
    return standaloneEnabled;
  }
  return false;
};
